import { useEffect, useRef } from "react";

import { PageLayout } from "@/components/layout";
import { toast } from "@/components/ui/Toast/useToast";
import { useChatThreadSession } from "@/hooks";
import { useChatStore } from "@/store/chat/chat.store";
import { useShallow } from "zustand/react/shallow";
import MessageInput from "./MessageInput";
import MessageList from "./MessageList";
import { MessageListSkeleton } from "./MessageListSkeleton";

/**
 * ChatThreadView - Displays an existing chat thread conversation.
 * This component only renders for threads with a valid threadId.
 * New chat creation is handled by SectionNewChatPage (/new route).
 */
export function ChatThreadView() {
  const { threadContext } = useChatStore(
    useShallow((state) => ({
      threadContext: state.activeChatThread,
    }))
  );
  const { status, messages, error, isReady, sendMessage, retryMessage } = useChatThreadSession(threadContext);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const hasThreadId = Boolean(threadContext?.threadId);

  // Listen for retry message events
  useEffect(() => {
    const handleRetryMessage = (event: CustomEvent<{ messageId: string }>) => {
      retryMessage(event.detail.messageId);
    };

    window.addEventListener("retry-message", handleRetryMessage as EventListener);
    return () => {
      window.removeEventListener("retry-message", handleRetryMessage as EventListener);
    };
  }, [retryMessage]);

  // Show connection/chat errors as toasts — deduplicated so that repeated
  // identical errors (e.g. repeated WebSocket reconnect failures) don't stack.
  const toastedErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (error && error !== toastedErrorRef.current) {
      toastedErrorRef.current = error;
      toast({ variant: "destructive", title: "Connection error", description: error });
    }
    if (!error) {
      toastedErrorRef.current = null;
    }
  }, [error]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (hasThreadId && messages.length > 0) {
      // Use requestAnimationFrame for better performance and to ensure DOM has updated
      requestAnimationFrame(() => {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 50);
      });
    }
  }, [messages, hasThreadId]);

  // This component only renders for existing threads with a threadId
  if (!threadContext || !hasThreadId) {
    return (
      <PageLayout>
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex flex-1 items-center justify-center bg-muted text-muted-foreground">
            <p>Select a chat to get started.</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl">
        <div
          className="flex-1 overflow-y-auto bg-muted"
          data-tour="chat-messages"
        >
          {status === "connecting" && messages.length === 0 ? (
            <MessageListSkeleton />
          ) : (
            <MessageList
              messages={messages}
              variant="stack"
            />
          )}
          <div ref={messagesEndRef} />
        </div>
        <div data-tour="chat-input">
          <MessageInput
            onSend={sendMessage}
            disabled={!isReady}
            threadId={threadContext.threadId ?? undefined}
            companyId={threadContext.companyId}
          />
        </div>
      </div>
    </PageLayout>
  );
}
